import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { BoxIcon, TagIcon, PackageIcon, CheckIcon, TruckIcon, ShoppingCartIcon, AlertCircleIcon, BookOpenIcon, GlobeIcon, ClockIcon, UserIcon } from 'lucide-react';
import { bookBoxes } from '../../BookBoxDetails';
import { useDarkMode } from '../../context/DarkModeContext';
import { formatCurrency } from './utils';
import type { BookBox } from './types';
interface OverviewTabProps {
  bookBox: BookBox;
}
export const OverviewTab: React.FC<OverviewTabProps> = ({
  bookBox
}) => {
  const {
    darkMode
  } = useDarkMode();
  return <div className="space-y-8" data-id="element-7489" data-mp="ng52qejlj613">
      {/* Box Description */}
      <div data-id="element-7490" data-mp="ndimlqin3i79">
        <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7491" data-mp="ih1w4pnywrr0">
          About This Box
        </h2>
        <p className={`text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7492" data-mp="1ytgzy6kuoqea">
          {bookBox.description}
        </p>
      </div>

      {/* What's Included */}
      <div data-id="element-7493" data-mp="a0urr7q58gjh">
        <h3 className={`text-xl font-semibold mb-4 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7494" data-mp="a3gw6yhqmfxh">
          <PackageIcon className={`h-5 w-5 mr-2 ${darkMode ? 'text-purple-400' : 'text-indigo-500'}`} data-id="element-7495" data-mp="j1khbtot9aor" />
          What's Included in Each Box
        </h3>
        <div className={`rounded-lg p-6 ${darkMode ? 'bg-purple-900/20 border border-purple-800/30' : 'bg-indigo-50'}`} data-id="element-7496" data-mp="nysxxpub96nw">
          <ul className="space-y-3" data-id="element-7497" data-mp="3rixew1cmh18n">
            {bookBox.includedItems.map((item, index) => <li key={index} className="flex items-start" data-id="element-7498" data-mp="bb7bxms8emi" data-mpi={index}>
                <CheckIcon className={`h-5 w-5 mr-3 flex-shrink-0 mt-0.5 ${darkMode ? 'text-purple-400' : 'text-indigo-600'}`} data-id="element-7499" data-mp="52b0crss6cjq" />
                <span className={darkMode ? 'text-gray-300' : 'text-gray-700'} data-id="element-7500" data-mp="33rrynl2rs6xt">
                  {item}
                </span>
              </li>)}
          </ul>
        </div>
      </div>

      {/* Contents Overview */}
      <div data-id="element-7501" data-mp="30oc3wr5ubqpr">
        <h3 className={`text-xl font-semibold mb-4 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7502" data-mp="2wpd0xc3o640y">
          <BookOpenIcon className={`h-5 w-5 mr-2 ${darkMode ? 'text-purple-400' : 'text-indigo-500'}`} data-id="element-7503" data-mp="3afgk1u795hm6" />
          Box Overview
        </h3>
        <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7504" data-mp="2hrkmkn9uzloc">
          {bookBox.contentsOverview}
        </p>
      </div>

      {/* Shipping Info */}
      <div data-id="element-7505" data-mp="2rpqoh4vo0rt7">
        <h3 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7506" data-mp="2rqjnfjodud0s">
          <TruckIcon className={`h-5 w-5 mr-2 ${darkMode ? 'text-purple-400' : 'text-indigo-500'}`} data-id="element-7507" data-mp="2mpcf9jibvbtl" />
          Shipping Information
        </h3>
        <div className={`p-5 rounded-lg ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50'}`} data-id="element-7508" data-mp="21hmio4mto6k">
          <p className={`mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`} data-id="element-7509" data-mp="1jwrs606mp9kz">
            <span className="font-medium" data-id="element-7510" data-mp="1oyh3lgxrjp9l">Ships to:</span> {bookBox.shipping}
          </p>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-7511" data-mp="1ayerycg1ts2s">
            Boxes typically ship between the 15th-20th of each month. You'll
            receive a shipping confirmation email with tracking information once
            your box is on its way.
          </p>
        </div>
      </div>

      {/* Tags */}
      <div className="mt-6" data-id="element-7512" data-mp="27pqe9tj9sk2r">
        <h3 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7513" data-mp="1tx0tfsfh9l78">
          <TagIcon className={`h-5 w-5 mr-2 ${darkMode ? 'text-purple-400' : 'text-indigo-500'}`} data-id="element-7514" data-mp="1yu10o6o2ctzx" />
          Product Categories
        </h3>
        <div className="flex flex-wrap gap-2" data-id="element-7515" data-mp="2rcekv3gh8hgj">
          {bookBox.tags.map((tag, index) => <span key={`available-${index}`} className={`px-3 py-1 rounded-full text-sm flex items-center ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'}`} data-id="element-7516" data-mp="12qlfyx82yl18" data-mpi={index}>
              <TagIcon className="h-3 w-3 mr-1" data-id="element-7517" data-mp="tqf7xqv5id9g" />
              {tag}
            </span>)}
          {['Exclusive Editions', 'Art Prints'].map((tag, index) => <span key={`upcoming-${index}`} className={`px-3 py-1 rounded-full text-sm flex items-center border ${darkMode ? 'bg-purple-900/30 text-purple-300 border-purple-700' : 'bg-indigo-50 text-indigo-600 border-indigo-200'}`} data-id="element-7518" data-mp="1lh35ksxm90id" data-mpi={index}>
              <ClockIcon className="h-3 w-3 mr-1" data-id="element-7519" data-mp="1clwy7pip6adq" />
              {tag} (Coming Soon)
            </span>)}
        </div>
        <p className={`mt-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-7520" data-mp="2552dlw0sptk3">
          More product categories will be added as new boxes become available.
        </p>
      </div>

      {/* Content Tags Section */}
      <div className="mt-6" data-id="element-7521" data-mp="27qtf8270tj2q">
        <h3 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7522" data-mp="2co20ibngu387">
          <BookOpenIcon className={`h-5 w-5 mr-2 ${darkMode ? 'text-purple-400' : 'text-indigo-500'}`} data-id="element-7523" data-mp="2hm3ggnplhmfg" />
          Content Information
        </h3>

        {/* Tropes */}
        <div className="mb-4" data-id="element-7524" data-mp="2qomhr3dqfqzl">
          <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-7525" data-mp="2qnbcez4n2b6d">
            Common Tropes:
          </h4>
          <div className="flex flex-wrap gap-2" data-id="element-7526" data-mp="30de5t0fo9r58">
            {['Enemies to Lovers', 'Forced Proximity', 'Slow Burn', 'Found Family'].map((trope, index) => <span key={`trope-${index}`} className={`px-3 py-1 rounded-full text-sm ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'}`} data-id="element-7527" data-mp="39fx3z78ijfl5" data-mpi={index}>
                {trope}
              </span>)}
          </div>
        </div>

        {/* Representation */}
        <div className="mb-4" data-id="element-7528" data-mp="1a7rh22oz6hou">
          <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-7529" data-mp="1o18x3digdca4">
            Representation:
          </h4>
          <div className="flex flex-wrap gap-2" data-id="element-7530" data-mp="1xz56o0vqrz64">
            {['LGBTQ+', 'BIPOC', 'Disability', 'Neurodiversity'].map((rep, index) => <span key={`rep-${index}`} className={`px-3 py-1 rounded-full text-sm ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-50 text-green-700'}`} data-id="element-7531" data-mp="1szaja7b6drpt" data-mpi={index}>
                  {rep}
                </span>)}
          </div>
        </div>

        {/* Content Warnings */}
        <div className="mb-2" data-id="element-7532" data-mp="7mz9wmpy5tkv">
          <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-7533" data-mp="7nsh1ostil3j">
            Content Warnings:
          </h4>
          <div className="flex flex-wrap gap-2" data-id="element-7534" data-mp="2o9p505qey0kf">
            {['Sexual Content', 'Strong Language', 'Violence', 'Death'].map((warning, index) => <span key={`warning-${index}`} className={`px-3 py-1 rounded-full text-sm flex items-center ${darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-50 text-amber-700'}`} data-id="element-7535" data-mp="2afobpri8d5rg" data-mpi={index}>
                  <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-7536" data-mp="25ixz5e5mwsas" />
                  {warning}
                </span>)}
          </div>
        </div>

        {/* Age Rating */}
        <div className={`mt-4 p-3 rounded-lg flex items-center ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50'}`} data-id="element-7537" data-mp="1vjyr9xsp10ou">
          <UserIcon className={`h-5 w-5 mr-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7538" data-mp="3lutib6lhfogc" />
          <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`} data-id="element-7539" data-mp="2dp66j30v1juf">
            Age Rating: 18+
          </span>
          <span className={`ml-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-7540" data-mp="28quxguie3dd9">
            (Due to mature themes and content)
          </span>
        </div>
      </div>

      {/* How It Works */}
      <div data-id="element-7541" data-mp="1uyogj67ex2cr">
        <h3 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7542" data-mp="1uyes3n20n396">
          How It Works
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-7543" data-mp="1pya6xjq5xkcm">
          {[{
          icon: ShoppingCartIcon,
          title: 'Subscribe',
          desc: 'Choose your subscription length and any customization options'
        }, {
          icon: BoxIcon,
          title: 'Receive',
          desc: 'Your curated box arrives monthly with carefully selected items'
        }, {
          icon: BookOpenIcon,
          title: 'Enjoy',
          desc: 'Dive into your new books and enjoy your themed goodies'
        }].map(({
          icon: Icon,
          title,
          desc
        }) => <div key={title} className={`p-5 rounded-lg text-center ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50'}`} data-id="element-7544" data-mp="3rohfild2vkxr">
              <div className={`h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-3 ${darkMode ? 'bg-purple-900/40' : 'bg-indigo-100'}`} data-id="element-7545" data-mp="2zxuttbnstthr">
                <Icon className={`h-6 w-6 ${darkMode ? 'text-purple-400' : 'text-indigo-600'}`} data-id="element-7546" data-mp="38y9fwd8l7cmb" />
              </div>
              <h4 className={`font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7547" data-mp="3dy5jwchab41v">
                {title}
              </h4>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-7548" data-mp="t6lvfharfcke">
                {desc}
              </p>
            </div>)}
        </div>
      </div>

      {/* Similar Book Boxes */}
      <div data-id="element-7549" data-mp="2o71qvfrjgau9">
        <h3 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7550" data-mp="2o6rwzh3dhjja">
          You Might Also Like
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-7551" data-mp="2x051zhepoblq">
          {bookBoxes.filter(box => box.boxName !== bookBox.boxName).slice(0, 3).map(box => <div key={box.boxName} className={`rounded-lg overflow-hidden border transition-shadow hover:shadow-md ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-7552" data-mp="25aaabe3ds9tw">
                <div className={`h-32 flex items-center justify-center text-white ${darkMode ? 'bg-purple-900/50' : 'bg-gradient-to-r from-indigo-400 to-indigo-500'}`} data-id="element-7553" data-mp="3kr0ygru43uzb">
                  <BoxIcon className="h-12 w-12 opacity-75" data-id="element-7554" data-mp="3o4s2rruq8gc" />
                </div>
                <div className="p-4" data-id="element-7555" data-mp="3uupge3qiv29o">
                  <Link to={`/book-boxes/${encodeURIComponent(box.boxName)}`} className={`text-lg font-semibold hover:underline ${darkMode ? 'text-white hover:text-purple-400' : 'text-gray-900 hover:text-indigo-600'}`} data-id="element-7556" data-mp="3uvqs8afd3ssg">
                    {box.boxName}
                  </Link>
                  <p className={`text-sm mt-1 line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-7557" data-mp="3h17x8k1qzrk6">
                    {box.theme}
                  </p>
                  <div className="flex justify-between items-center mt-3" data-id="element-7558" data-mp="3777lw0fvhq30">
                    <div className={`flex items-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7559" data-mp="33546j0q321cg">
                      <GlobeIcon className="h-4 w-4 mr-1" data-id="element-7560" data-mp="11bn2s852pkmz" />
                      {box.locationCountry}
                    </div>
                    <div className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7561" data-mp="2uu3tz73hzigg">
                      {formatCurrency(box.price, box.currency)}
                    </div>
                  </div>
                </div>
              </div>)}
        </div>
      </div>
    </div>;
};